import type { Locale } from 'date-fns';
import { formatDistanceToNow } from 'date-fns';
import { Battery, BatteryLow, BatteryMedium, BatteryFull, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Device, Position } from '@/lib/traccar-api';

type TFn = (key: string, fallback?: string) => string;
const defaultT: TFn = (_key, fallback) => fallback ?? _key;

interface DeviceRowContentProps {
  device: Device;
  position?: Position;
  locale?: Locale;
  t?: TFn;
  indent?: boolean;
  wrap?: boolean;
}

export function DeviceRowContent({ device, position, locale, t = defaultT, indent = true, wrap = true }: DeviceRowContentProps) {
  const attrs = position?.attributes ?? {};
  const ignition = attrs.ignition as boolean | undefined;
  const blocked = attrs.blocked as boolean | undefined;
  const alarm = attrs.alarm as string | undefined;
  const batteryLevel = attrs.batteryLevel as number | undefined;
  const sat = attrs.sat as number | undefined;
  const gpsValid = position?.valid;
  const odometerM = attrs.odometer != null ? Number(attrs.odometer) : attrs.totalDistance != null ? Number(attrs.totalDistance) : undefined;
  const odometerKm = odometerM != null ? Math.round(odometerM / 1000).toLocaleString() : undefined;
  const power = attrs.power != null ? Number(attrs.power) : undefined;

  const lastUpdateTime = (() => {
    const deviceTime = device.lastUpdate ? new Date(device.lastUpdate).getTime() : 0;
    const positionTime = position?.fixTime ? new Date(position.fixTime).getTime() : 0;
    if (positionTime > deviceTime) return position!.fixTime;
    return device.lastUpdate ?? null;
  })();
  const lastUpdateText = lastUpdateTime
    ? formatDistanceToNow(new Date(lastUpdateTime), { addSuffix: true, locale })
    : '';

  const hasIndicators = ignition !== undefined || !!position || blocked !== undefined || (batteryLevel !== undefined && power == null) || !!alarm || !!position?.protocol || odometerKm != null || power != null;

  const ignLabel = ignition ? t('performance.on', 'ON') : t('performance.off', 'OFF');
  const noFixLabel = t('map.noFix', 'No fix');
  const blockedLabel = blocked ? t('map.blocked', 'Engine blocked') : t('map.unblocked', 'Engine unblocked');
  const batteryLabel = t('performance.battery', 'Battery');
  const satellitesLabel = t('performance.satellites', 'satellites');
  const alarmLabel = t('notifications.attributes.alarm', 'Alarm');
  const alarmValue = alarm ? t(`notifications.alarms.${alarm}`, alarm) : '';

  return (
    <>
      {hasIndicators && (
        <div {...(!wrap ? { 'data-indicators': '' } : {})} className={cn('flex items-center gap-2 mt-1.5 min-w-0', wrap ? 'flex-wrap' : 'flex-nowrap overflow-hidden', indent && 'ml-[18px]')}>
          {ignition !== undefined && (
            <span title={`${t('performance.ignition', 'Ignition')} ${ignLabel}`} className={cn('flex flex-shrink-0 items-center gap-0.5 text-[11px]', ignition ? 'text-green-600' : 'text-muted-foreground')}>
              <i className={ignition ? 'i-ignition-on' : 'i-ignition-off'} style={{ fontSize: '15px' }} />
              <span>{ignLabel}</span>
            </span>
          )}

          {position && (
            <span title={gpsValid === false ? noFixLabel : sat !== undefined ? `${sat} ${satellitesLabel}` : 'GPS'} className={cn('flex flex-shrink-0 items-center gap-0.5 text-[11px]', gpsValid === false ? 'text-red-500' : sat !== undefined && sat >= 4 ? 'text-green-600' : 'text-muted-foreground')}>
              <i className={gpsValid === false ? 'i-sat-off' : 'i-sat'} style={{ fontSize: '15px' }} />
              {gpsValid !== false && sat !== undefined && <span>{sat}</span>}
            </span>
          )}

          {blocked !== undefined && (
            <span title={blockedLabel} className={cn('flex flex-shrink-0 items-center gap-0.5 text-[11px]', blocked ? 'text-red-500' : 'text-green-600')}>
              <i className={blocked ? 'i-lock-2' : 'i-lock'} style={{ fontSize: '15px' }} />
            </span>
          )}

          {batteryLevel !== undefined && power == null && (() => {
            const BatteryIcon = batteryLevel > 75 ? BatteryFull : batteryLevel > 40 ? BatteryMedium : batteryLevel > 15 ? BatteryLow : Battery;
            const color = batteryLevel > 50 ? 'text-green-600' : batteryLevel > 20 ? 'text-yellow-600' : 'text-red-600';
            return (
              <span title={`${batteryLabel} ${Math.round(batteryLevel)}%`} className={cn('flex flex-shrink-0 items-center gap-0.5 text-[11px]', color)}>
                <BatteryIcon className="h-4 w-4" />
              </span>
            );
          })()}

          {alarm && (
            <span title={`${alarmLabel}: ${alarmValue}`} className="flex flex-shrink-0 items-center gap-0.5 text-[11px] text-red-500">
              <AlertTriangle className="h-3 w-3" />
              <span>{alarmValue}</span>
            </span>
          )}

          {power != null && (
            <span title={`Power ${power.toFixed(1)} V`} className="flex flex-shrink-0 items-center gap-0.5 text-[11px] text-muted-foreground">
              <i className="i-battery-con" style={{ fontSize: '15px' }} />
              <span>{power.toFixed(1)} V</span>
            </span>
          )}

          {odometerKm != null && (
            <span title={`Odometer ${odometerKm} km`} className="flex-shrink-0 text-[11px] text-muted-foreground">
              {odometerKm} km
            </span>
          )}

          {position?.protocol && (
            <span title={`Protocol: ${position.protocol}`} className="flex-shrink-0 text-[10px] px-1 py-0.5 rounded bg-muted text-muted-foreground font-mono leading-none">
              {position.protocol}
            </span>
          )}
        </div>
      )}

      {(position?.address || lastUpdateText) && (
        <div className={cn('flex items-center gap-2 mt-1 text-[11px] text-muted-foreground min-w-0', indent && 'ml-[18px]')}>
          {position?.address && <span title={position.address} className="truncate flex-1 min-w-0">{position.address}</span>}
          {lastUpdateText && <span className="flex-shrink-0 text-[10px]">{lastUpdateText}</span>}
        </div>
      )}
    </>
  );
}
